import type { AuthProviderProps } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import type { AppEnv } from "../hooks/useEnv";

const env = import.meta.env;

export function apiBaseUrl(appEnv: AppEnv): string {
  return appEnv === "prod"
    ? (env.VITE_BASE_API_URL_PROD as string)
    : (env.VITE_BASE_API_URL_DEV as string);
}

// Public customer-site base URL used to compose event-invite QR campaign URLs
// (https://<base>/<origin-slug>?code=<event-id>). Mirrors apiBaseUrl's env
// branch. Both envs default to the production customer site.
const DEFAULT_CUSTOMER_BASE_URL = "https://www.humancreator.com";

export function customerBaseUrl(appEnv: AppEnv): string {
  const value =
    appEnv === "prod"
      ? env.VITE_CUSTOMER_BASE_URL_PROD
      : env.VITE_CUSTOMER_BASE_URL_DEV;
  return (value as string) || DEFAULT_CUSTOMER_BASE_URL;
}

// Customer-site host for the public profile link shown on the creator detail
// page. Unlike customerBaseUrl (QR campaigns — intentionally prod for BOTH envs),
// the profile link must point at the matching environment's customer site so the
// dev support app links to dev.humancreator.com, prod to www.humancreator.com.
// Honors the same VITE_CUSTOMER_BASE_URL_* overrides when set.
const DEFAULT_CUSTOMER_PROFILE_BASE_URL_DEV = "https://dev.humancreator.com";
const DEFAULT_CUSTOMER_PROFILE_BASE_URL_PROD = "https://www.humancreator.com";

export function customerProfileBaseUrl(appEnv: AppEnv): string {
  const value =
    appEnv === "prod"
      ? env.VITE_CUSTOMER_BASE_URL_PROD
      : env.VITE_CUSTOMER_BASE_URL_DEV;
  return (
    (value as string) ||
    (appEnv === "prod"
      ? DEFAULT_CUSTOMER_PROFILE_BASE_URL_PROD
      : DEFAULT_CUSTOMER_PROFILE_BASE_URL_DEV)
  );
}

// Cognito hosted-UI's /logout endpoint is not OIDC-compliant — it expects
// `client_id` + `logout_uri` (not `post_logout_redirect_uri`). react-oidc-
// context's signoutRedirect() lands on a useless Cognito page; build the
// hosted-UI URL ourselves and navigate to it.
export function cognitoLogoutUrl(appEnv: AppEnv): string {
  const domain =
    appEnv === "prod"
      ? (env.VITE_COGNITO_DOMAIN_PROD ?? env.VITE_COGNITO_DOMAIN)
      : env.VITE_COGNITO_DOMAIN;
  const clientId =
    appEnv === "prod"
      ? (env.VITE_COGNITO_CLIENT_ID_PROD ?? env.VITE_COGNITO_CLIENT_ID)
      : env.VITE_COGNITO_CLIENT_ID;
  // Derive from the live origin so support.humancreator.com and the *.cloudfront.net
  // alias each redirect back to themselves (state nonce is keyed by origin).
  const logoutUri = `${window.location.origin}/`;
  const params = new URLSearchParams({
    client_id: clientId as string,
    logout_uri: logoutUri,
  });
  return `https://${domain}/logout?${params.toString()}`;
}

export function oidcConfig(appEnv: AppEnv): AuthProviderProps {
  // Each env has its own Cognito user pool. Auth values can either be supplied
  // per-env (VITE_COGNITO_*_PROD) or fall back to the dev set (single-pool dev).
  const authority =
    appEnv === "prod"
      ? (env.VITE_COGNITO_AUTHORITY_PROD ?? env.VITE_COGNITO_AUTHORITY)
      : env.VITE_COGNITO_AUTHORITY;
  const clientId =
    appEnv === "prod"
      ? (env.VITE_COGNITO_CLIENT_ID_PROD ?? env.VITE_COGNITO_CLIENT_ID)
      : env.VITE_COGNITO_CLIENT_ID;

  return {
    authority: authority as string,
    client_id: clientId as string,
    // Pin redirect/logout URIs to the live origin so users on support.humancreator.com
    // get redirected back to the same origin (state nonce is keyed per-origin).
    redirect_uri: `${window.location.origin}/signin/callback`,
    post_logout_redirect_uri: `${window.location.origin}/`,
    response_type: "code",
    scope: "openid email profile",
    automaticSilentRenew: true,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    onSigninCallback: () => {
      // Strip OIDC params from the URL after callback completes.
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };
}
